import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import { type ChapterFM, type ChapterSettingsUpdateRequest, type ChapterSummaryFM, type Venue, type VenueRequest } from './chapter.model';

/**
 * Covers the four {@code /chapter/*} endpoints the Chapter Settings screen
 * needs (TDD 02 §6.1/§7.2, TDD 10 §6.1/§7) — one admin screen, id-addressed
 * rather than resolved from "my chapter" implicitly (see
 * {@code ChapterController}'s javadoc for why).
 */
@Injectable({
  providedIn: 'root'
})
export class ChapterService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiBaseUrl}/chapter`;

  /** {@code GET /chapter/{id}} — {@code ChapterFM} projection (name, status, UPI QR/VPA, both fee amounts). */
  getChapter(id: string): Observable<ChapterFM> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.get<ChapterFM>(`${this.apiUrl}/${id}`, { headers });
  }

  /**
   * {@code GET /chapter/{id}/summary} — the dashboard-header projection (name, district, number,
   * member count). Open to any authenticated member (no {@code @PreAuthorize} on the backend
   * endpoint), unlike {@link getChapter}. No {@code X-Spinner} header — this loads inline as part
   * of the dashboard's own composed fetch, not behind the global loading overlay.
   */
  getSummary(id: string): Observable<ChapterSummaryFM> {
    return this.http.get<ChapterSummaryFM>(`${this.apiUrl}/${id}/summary`);
  }

  /** {@code POST /chapter/{id}/upi-qr} — multipart upload/replace, same staged-`File`/`FormData` pattern as `MemberFormComponent.onPhotoSelect`. */
  uploadUpiQr(id: string, file: File): Observable<ChapterFM> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ChapterFM>(`${this.apiUrl}/${id}/upi-qr`, formData, { headers });
  }

  /** {@code POST /chapter/{id}/certificate} — multipart upload/replace (image or PDF), same staged-`File`/`FormData` pattern as {@link uploadUpiQr}. */
  uploadCertificate(id: string, file: File): Observable<ChapterFM> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ChapterFM>(`${this.apiUrl}/${id}/certificate`, formData, { headers });
  }

  /** {@code DELETE /chapter/{id}/certificate} — removes the certificate without replacing it. */
  deleteCertificate(id: string): Observable<ChapterFM> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.delete<ChapterFM>(`${this.apiUrl}/${id}/certificate`, { headers });
  }

  /** {@code POST /chapter/{id}/logo} — uploads or replaces the chapter's toolbar logo (image only). */
  uploadLogo(id: string, file: File): Observable<ChapterFM> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ChapterFM>(`${this.apiUrl}/${id}/logo`, formData, { headers });
  }

  /** {@code DELETE /chapter/{id}/logo} — removes the chapter's logo; the toolbar falls back to the common one. */
  deleteLogo(id: string): Observable<ChapterFM> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.delete<ChapterFM>(`${this.apiUrl}/${id}/logo`, { headers });
  }

  /**
   * {@code PUT /chapter/{id}/settings} — the whole Save button in one write: currency,
   * UPI VPA and both membership fee amounts.
   *
   * Replaces the three endpoints this screen used to call concurrently
   * (`/upi-id`, `/currency`, `/membership-fee`), each of which saved the entire chapter
   * document and so overwrote the others. Every field is sent on every save, **including
   * nulls** — a cleared field clears the stored value, matching what the form shows. This
   * body deliberately does NOT go through `SharedService.removeNullFields()`, which would
   * strip the very nulls that carry the "cleared" intent.
   */
  updateSettings(id: string, request: ChapterSettingsUpdateRequest): Observable<ChapterFM> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.put<ChapterFM>(`${this.apiUrl}/${id}/settings`, request, { headers });
  }

  /** The chapter's saved venues, by name (September 2026). */
  getVenues(chapterId: string): Observable<Venue[]> {
    return this.http.get<Venue[]>(`${this.apiUrl}/${chapterId}/venues`);
  }

  addVenue(chapterId: string, request: VenueRequest): Observable<Venue> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.post<Venue>(`${this.apiUrl}/${chapterId}/venues`, request, { headers });
  }

  /** Upcoming events held there move with it, and members are told. */
  updateVenue(chapterId: string, venueId: string, request: VenueRequest): Observable<Venue> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.put<Venue>(`${this.apiUrl}/${chapterId}/venues/${venueId}`, request, { headers });
  }

  /** Hides it from the list; events held or booked there keep it. */
  removeVenue(chapterId: string, venueId: string): Observable<void> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.delete<void>(`${this.apiUrl}/${chapterId}/venues/${venueId}`, { headers });
  }
}
